import { useState, useEffect } from "react";

export default function PricingFilter({
	filters,
	setFilters,
}) {
	// Local state — inputs never touch `filters` until Apply is clicked.
	// This stops the API from firing on every keystroke.
	const [localPerPerson, setLocalPerPerson] = useState({
		enabled: !!filters.perPersonEnabled,
		min: filters.minSpendPerPerson?.min || "",
		max: filters.minSpendPerPerson?.max || "",
	});
	const [localVenueRental, setLocalVenueRental] = useState({
		enabled: !!filters.venueRentalEnabled,
		min: filters.venueRental?.min || "",
		max: filters.venueRental?.max || "",
	});

	// Sync local state when filters are cleared externally (e.g. Reset All)
	useEffect(() => {
		setLocalPerPerson({
			enabled: !!filters.perPersonEnabled,
			min: filters.minSpendPerPerson?.min || "",
			max: filters.minSpendPerPerson?.max || "",
		});
	}, [
		filters.perPersonEnabled,
		filters.minSpendPerPerson?.min,
		filters.minSpendPerPerson?.max,
	]);

	useEffect(() => {
		setLocalVenueRental({
			enabled: !!filters.venueRentalEnabled,
			min: filters.venueRental?.min || "",
			max: filters.venueRental?.max || "",
		});
	}, [
		filters.venueRentalEnabled,
		filters.venueRental?.min,
		filters.venueRental?.max,
	]);

	const handleApply = () => {
		setFilters((prev) => ({
			...prev,
			perPersonEnabled: localPerPerson.enabled,
			minSpendPerPerson: { min: localPerPerson.min, max: localPerPerson.max },
			venueRentalEnabled: localVenueRental.enabled,
			venueRental: { min: localVenueRental.min, max: localVenueRental.max },
		}));
	};

	const handleClear = () => {
		setLocalPerPerson({ enabled: false, min: "", max: "" });
		setLocalVenueRental({ enabled: false, min: "", max: "" });
		setFilters((prev) => ({
			...prev,
			perPersonEnabled: false,
			minSpendPerPerson: { min: "", max: "" },
			venueRentalEnabled: false,
			venueRental: { min: "", max: "" },
		}));
	};

	return (
		<div className="space-y-4 p-4 border rounded shadow-sm bg-white">
			{/* Budget Per Person */}
			<div className="flex items-center gap-3">
				<input
					type="radio"
					name="spendOption"
					className="form-radio h-4 w-4 text-blue-600"
					checked={localPerPerson.enabled}
					onChange={(e) => {
						setLocalPerPerson((prev) => ({ ...prev, enabled: e.target.checked }));
						setLocalVenueRental((prev) => ({ ...prev, enabled: false }));
					}}
				/>
				<label className="w-full">Budget Per Person</label>
				<div className="flex flex-1 items-center justify-end gap-3">
					<input
						type="number"
						placeholder="Min"
						value={localPerPerson.min}
						onChange={(e) =>
							setLocalPerPerson((prev) => ({ ...prev, min: e.target.value }))
						}
						disabled={!localPerPerson.enabled}
						className="border p-1 rounded w-20"
					/>
					<input
						type="number"
						placeholder="Max"
						value={localPerPerson.max}
						onChange={(e) =>
							setLocalPerPerson((prev) => ({ ...prev, max: e.target.value }))
						}
						disabled={!localPerPerson.enabled}
						className="border p-1 rounded w-20"
					/>
				</div>
			</div>

			{/* Budget Per Event */}
			<div className="flex items-center gap-3">
				<input
					type="radio"
					name="venueRentalToggle"
					className="form-radio h-4 w-4 text-blue-600"
					checked={localVenueRental.enabled}
					onChange={(e) => {
						setLocalVenueRental((prev) => ({ ...prev, enabled: e.target.checked }));
						setLocalPerPerson((prev) => ({ ...prev, enabled: false }));
					}}
				/>
				<label className="w-full">Budget Per Event</label>
				<div className="flex flex-1 items-center justify-end gap-3">
					<input
						type="number"
						placeholder="Min"
						value={localVenueRental.min}
						onChange={(e) =>
							setLocalVenueRental((prev) => ({ ...prev, min: e.target.value }))
						}
						disabled={!localVenueRental.enabled}
						className="border p-1 rounded w-20"
					/>
					<input
						type="number"
						placeholder="Max"
						value={localVenueRental.max}
						onChange={(e) =>
							setLocalVenueRental((prev) => ({ ...prev, max: e.target.value }))
						}
						disabled={!localVenueRental.enabled}
						className="border p-1 rounded w-20"
					/>
				</div>
			</div>

			{/* Clear (left) + Apply (right) */}
			<div className="flex items-center gap-3 pt-1">
				<button
					type="button"
					onClick={handleClear}
					className="flex-1 border border-gray-300 text-gray-600 py-2 rounded-md text-sm hover:bg-gray-50"
				>
					Clear
				</button>
				<button
					type="button"
					onClick={handleApply}
					className="flex-1 bg-[#FE4747] text-white py-2 rounded-md text-sm hover:bg-red-600"
				>
					Apply
				</button>
			</div>
		</div>
	);
}
